/**
 * Cookie Consent Manager
 *
 * Centralises consent state for analytics/tracking scripts using a
 * region-based model that matches standard industry practice:
 *
 *   - EEA / UK / Switzerland (and unknown regions): opt-in. Trackers stay
 *     off until the visitor explicitly accepts (GDPR Art. 6(1)(a)).
 *   - United States / rest of world: opt-out. Analytics may run by default,
 *     the banner provides notice, and a working "Reject" honours CCPA/CPRA.
 *
 * Region is inferred client-side from the browser timezone (no extra infra,
 * no IP lookup). It is intentionally conservative: anything that looks
 * European — or that we cannot classify — is treated as opt-in.
 */

import { browser } from '$app/environment';

const CONSENT_KEY = 'quilty-cookie-consent';
const CONSENT_DATE_KEY = 'quilty-cookie-consent-date';

export type ConsentStatus = 'accepted' | 'rejected' | null;

/** Which consent model applies to the current visitor. */
export type ConsentRegion = 'opt_in' | 'opt_out';

type ConsentCallback = (status: ConsentStatus) => void;

const listeners: Set<ConsentCallback> = new Set();

/**
 * IANA timezones that map to opt-in (GDPR-style) jurisdictions but are NOT
 * under the `Europe/` prefix. Covers EEA territories in other TZ regions:
 * Spain's Canary Islands, Portugal's Azores/Madeira, Iceland, and Cyprus.
 */
const EEA_EXTRA_TIMEZONES: ReadonlySet<string> = new Set([
  'Atlantic/Canary',
  'Atlantic/Azores',
  'Atlantic/Madeira',
  'Atlantic/Reykjavik',
  'Asia/Nicosia',
  'Asia/Famagusta',
]);

/**
 * Infer the consent region from the browser timezone. Every `Europe/*` zone
 * is treated as opt-in (this deliberately includes a few non-EEA European
 * zones — erring toward the stricter model is always safe). Unknown or
 * unreadable timezones also default to opt-in.
 */
export function detectConsentRegion(): ConsentRegion {
  if (!browser) return 'opt_in';
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    if (!tz) return 'opt_in';
    if (tz.startsWith('Europe/')) return 'opt_in';
    if (EEA_EXTRA_TIMEZONES.has(tz)) return 'opt_in';
    return 'opt_out';
  } catch {
    return 'opt_in';
  }
}

/**
 * Returns the stored consent decision from localStorage.
 * Returns null if no explicit decision has been made yet.
 */
export function getConsentStatus(): ConsentStatus {
  if (!browser) return null;
  const value = localStorage.getItem(CONSENT_KEY);
  if (value === 'accepted' || value === 'rejected') return value;
  return null;
}

/**
 * Whether analytics/advertising trackers are allowed to run for this visitor.
 *
 *   - Explicit "accepted"  -> always true
 *   - Explicit "rejected"  -> always false
 *   - No decision yet      -> true in opt-out regions, false in opt-in regions
 *
 * Always false during SSR (no window/timezone available).
 */
export function hasAnalyticsConsent(): boolean {
  if (!browser) return false;
  const status = getConsentStatus();
  if (status === 'accepted') return true;
  if (status === 'rejected') return false;
  return detectConsentRegion() === 'opt_out';
}

/**
 * Whether the consent banner should be shown. Shown whenever no explicit
 * decision has been recorded — both regions need the notice (opt-in to
 * collect consent, opt-out to advertise the reject option).
 */
export function shouldShowConsentBanner(): boolean {
  if (!browser) return false;
  return getConsentStatus() === null;
}

/**
 * Register a callback that fires whenever consent status changes.
 * Returns an unsubscribe function.
 */
export function onConsentChange(callback: ConsentCallback): () => void {
  listeners.add(callback);
  return () => {
    listeners.delete(callback);
  };
}

const openRequests: Set<() => void> = new Set();

/**
 * Ask the consent banner to reappear so the visitor can change an earlier
 * decision (footer "Cookie settings" link).
 */
export function openConsentSettings(): void {
  for (const cb of openRequests) cb();
}

/** Register the banner as the handler for `openConsentSettings()`. */
export function onConsentSettingsRequest(callback: () => void): () => void {
  openRequests.add(callback);
  return () => {
    openRequests.delete(callback);
  };
}

/**
 * Record the user's consent decision and notify listeners.
 * Called by CookieConsent component.
 */
export function setConsent(status: 'accepted' | 'rejected'): void {
  if (!browser) return;
  localStorage.setItem(CONSENT_KEY, status);
  localStorage.setItem(CONSENT_DATE_KEY, new Date().toISOString());
  for (const cb of listeners) {
    try {
      cb(status);
    } catch (e) {
      console.error('[ConsentManager] Listener error:', e);
    }
  }
}
